/**
 * Minimal "countdown-timer" script that never redirects or clears cookies.
 * It just shows a ticking countdown if the date is in the future, or "00" if it's already expired.
 *
 * Usage in Liquid:
 *   <countdown-timer
 *     data-date="DD-MM-YYYY"  // e.g. "03-03-2025"
 *     data-time="HH:MM"       // e.g. "23:59"  (optional)
 *     data-timezone="+1"      // e.g. "+1"    (optional)
 *   >
 *     <div class="days">
 *       <span class="countdown-timer--column--number">0</span>
 *       <span class="countdown-timer--column--text">Days</span>
 *     </div>
 *     <div class="hours">...</div>
 *     <div class="minutes">...</div>
 *     <div class="seconds">...</div>
 *   </countdown-timer>
 */

if (!customElements.get('countdown-timer')) {
  class CountdownTimer extends HTMLElement {
    constructor() {
      super();

      // 1) Parse date/time from <countdown-timer data-date="DD-MM-YYYY" data-time="HH:MM" data-timezone="+X">
      const { date, time, timezone } = this.dataset || {};
      if (!date) {
        console.warn('[CountdownTimer] Missing data-date attribute. Timer disabled.');
        this.countDownDate = null;
        return;
      }

      // We expect "DD-MM-YYYY"
      const [d, m, y] = date.split('-');
      if (!d || !m || !y) {
        console.warn('[CountdownTimer] data-date format invalid. Expected "DD-MM-YYYY". Got:', date);
        this.countDownDate = null;
        return;
      }

      const day   = parseInt(d, 10);
      const month = parseInt(m, 10); // 1..12
      const year  = parseInt(y, 10);

      // Default hour/minute if data-time is missing
      let tarHour = 0;
      let tarMin  = 0;
      if (time) {
        const [hh, mm] = time.split(':');
        tarHour = parseInt(hh, 10) || 0;
        tarMin  = parseInt(mm, 10) || 0;
      }

      // Local fallback ignoring timezone
      this.countDownDate = new Date(year, month - 1, day, tarHour, tarMin, 0, 0).getTime();

      // If a timezone was provided, try to parse with "GMT+X"
      if (timezone) {
        const tzStr   = `GMT${timezone}`; // e.g. "GMT+2"
        const testStr = `${month}/${day}/${year} ${tarHour}:${tarMin} ${tzStr}`;
        const parsed  = new Date(testStr);
        if (!isNaN(parsed.getTime())) {
          this.countDownDate = parsed.getTime();
        }
      }

      this._interval = null;
    }

    connectedCallback() {
      // If invalid date, skip
      if (!this.countDownDate) return;

      // 2) Grab references to DOM elements inside <countdown-timer>
      this.$days    = this.querySelector('.days .countdown-timer--column--number');
      this.$hours   = this.querySelector('.hours .countdown-timer--column--number');
      this.$minutes = this.querySelector('.minutes .countdown-timer--column--number');
      this.$seconds = this.querySelector('.seconds .countdown-timer--column--number');

      const hasAllElements = this.$days && this.$hours && this.$minutes && this.$seconds;

      // 3) Update the countdown every second
      this._interval = setInterval(() => {
        const now      = Date.now();
        const distance = this.countDownDate - now;

        // If timer ended or is negative, set "00" and stop
        if (distance <= 0) {
          if (hasAllElements) {
            this.$days.textContent    = '00';
            this.$hours.textContent   = '00';
            this.$minutes.textContent = '00';
            this.$seconds.textContent = '00';
          }
          clearInterval(this._interval);
          this._interval = null;
          return;
        }

        // Still time left, update
        if (hasAllElements) {
          const days    = Math.floor(distance / (1000 * 60 * 60 * 24));
          const hours   = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
          const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
          const seconds = Math.floor((distance % (1000 * 60)) / 1000);

          this.$days.textContent    = CountdownTimer.padZero(days);
          this.$hours.textContent   = CountdownTimer.padZero(hours);
          this.$minutes.textContent = CountdownTimer.padZero(minutes);
          this.$seconds.textContent = CountdownTimer.padZero(seconds);
        }
      }, 1000);
    }

    disconnectedCallback() {
      // If element is removed, clear the interval
      if (this._interval) {
        clearInterval(this._interval);
        this._interval = null;
      }
    }

    // Helper: zero-pad single-digit values
    static padZero(num) {
      return (num < 10 && num >= 0) ? '0' + num : String(num);
    }
  }

  customElements.define('countdown-timer', CountdownTimer);
}